import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Decision, Choice, Variable, SoftFactor, SimulationResult } from './types'
import { analytics } from './lib/analytics'

interface StoreState {
  decisions: Decision[]
  results: Record<string, SimulationResult>

  addDecision: (d: Omit<Decision, 'id' | 'created_at' | 'updated_at' | 'choices'> & { choices?: Choice[] }) => string
  updateDecision: (id: string, patch: Partial<Decision>) => void
  deleteDecision: (id: string) => void
  addChoice: (decisionId: string, name: string, notes?: string) => string
  updateChoice: (decisionId: string, choiceId: string, patch: Partial<Choice>) => void
  deleteChoice: (decisionId: string, choiceId: string) => void
  addVariable: (decisionId: string, choiceId: string, variable: Variable) => void
  updateVariable: (decisionId: string, choiceId: string, varId: string, patch: Partial<Variable>) => void
  deleteVariable: (decisionId: string, choiceId: string, varId: string) => void
  addSoftFactor: (decisionId: string, choiceId: string, sf: SoftFactor) => void
  updateSoftFactor: (decisionId: string, choiceId: string, sfId: string, patch: Partial<SoftFactor>) => void
  deleteSoftFactor: (decisionId: string, choiceId: string, sfId: string) => void
  saveResult: (decisionId: string, result: SimulationResult) => void
}

const uid = () => (crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2))

export const useStore = create<StoreState>()(
  persist(
    (set) => ({
      decisions: [],
      results: {},

      addDecision: (d) => {
        const id = uid()
        const now = new Date().toISOString()
        set((s) => ({
          decisions: [
            ...s.decisions,
            {
              id,
              name: d.name,
              horizon_years: d.horizon_years,
              discount_rate: d.discount_rate,
              risk_aversion: d.risk_aversion ?? 0.002,
              choices: d.choices ?? [],
              wealth: d.wealth,
              base_currency: d.base_currency,
              forked_from: d.forked_from,
              forked_from_name: d.forked_from_name,
              forked_from_author: d.forked_from_author,
              created_at: now,
              updated_at: now,
            },
          ],
        }))
        analytics.track('decision_created', {
          is_ai_generated: !!(d as any).is_ai_generated,
          is_fork: !!d.forked_from,
        })
        return id
      },
      updateDecision: (id, patch) =>
        set((s) => ({
          decisions: s.decisions.map((d) =>
            d.id === id ? { ...d, ...patch, updated_at: new Date().toISOString() } : d,
          ),
        })),
      deleteDecision: (id) =>
        set((s) => ({
          decisions: s.decisions.filter((d) => d.id !== id),
          results: Object.fromEntries(Object.entries(s.results).filter(([k]) => k !== id)),
        })),
      addChoice: (decisionId, name, notes = '') => {
        const cid = uid()
        set((s) => ({
          decisions: s.decisions.map((d) =>
            d.id === decisionId
              ? { ...d, choices: [...d.choices, { id: cid, name, notes, variables: [], softFactors: [] }] }
              : d,
          ),
        }))
        return cid
      },
      updateChoice: (decisionId, choiceId, patch) =>
        set((s) => ({
          decisions: s.decisions.map((d) =>
            d.id === decisionId
              ? {
                  ...d,
                  choices: d.choices.map((c) => (c.id === choiceId ? { ...c, ...patch } : c)),
                }
              : d,
          ),
        })),
      deleteChoice: (decisionId, choiceId) =>
        set((s) => ({
          decisions: s.decisions.map((d) =>
            d.id === decisionId
              ? { ...d, choices: d.choices.filter((c) => c.id !== choiceId) }
              : d,
          ),
        })),
      addVariable: (decisionId, choiceId, variable) =>
        set((s) => ({
          decisions: s.decisions.map((d) =>
            d.id === decisionId
              ? {
                  ...d,
                  choices: d.choices.map((c) =>
                    c.id === choiceId ? { ...c, variables: [...c.variables, variable] } : c,
                  ),
                }
              : d,
          ),
        })),
      updateVariable: (decisionId, choiceId, varId, patch) =>
        set((s) => ({
          decisions: s.decisions.map((d) =>
            d.id === decisionId
              ? {
                  ...d,
                  choices: d.choices.map((c) =>
                    c.id === choiceId
                      ? {
                          ...c,
                          variables: c.variables.map((v) =>
                            v.id === varId ? { ...v, ...patch } : v,
                          ),
                        }
                      : c,
                  ),
                }
              : d,
          ),
        })),
      deleteVariable: (decisionId, choiceId, varId) =>
        set((s) => ({
          decisions: s.decisions.map((d) =>
            d.id === decisionId
              ? {
                  ...d,
                  choices: d.choices.map((c) =>
                    c.id === choiceId
                      ? { ...c, variables: c.variables.filter((v) => v.id !== varId) }
                      : c,
                  ),
                }
              : d,
          ),
        })),
      addSoftFactor: (decisionId, choiceId, sf) =>
        set((s) => ({
          decisions: s.decisions.map((d) =>
            d.id === decisionId
              ? {
                  ...d,
                  choices: d.choices.map((c) =>
                    c.id === choiceId
                      ? { ...c, softFactors: [...(c.softFactors ?? []), sf] }
                      : c,
                  ),
                }
              : d,
          ),
        })),
      updateSoftFactor: (decisionId, choiceId, sfId, patch) =>
        set((s) => ({
          decisions: s.decisions.map((d) =>
            d.id === decisionId
              ? {
                  ...d,
                  choices: d.choices.map((c) =>
                    c.id === choiceId
                      ? {
                          ...c,
                          softFactors: (c.softFactors ?? []).map((sf) =>
                            sf.id === sfId ? { ...sf, ...patch } : sf,
                          ),
                        }
                      : c,
                  ),
                }
              : d,
          ),
        })),
      deleteSoftFactor: (decisionId, choiceId, sfId) =>
        set((s) => ({
          decisions: s.decisions.map((d) =>
            d.id === decisionId
              ? {
                  ...d,
                  choices: d.choices.map((c) =>
                    c.id === choiceId
                      ? { ...c, softFactors: (c.softFactors ?? []).filter((sf) => sf.id !== sfId) }
                      : c,
                  ),
                }
              : d,
          ),
        })),
      saveResult: (decisionId, result) =>
        set((s) => ({ results: { ...s.results, [decisionId]: result } })),
    }),
    { name: 'lifeodds-store' },
  ),
)

export { uid }
